import type { ReactNode } from 'react';
import { useHref, useNavigate } from 'react-router-dom';
import { RouterProvider } from 'react-aria-components';
import AppBar from './AppBar';
import type { NavItem } from './AppBar';
import PracticeRibbon from '../PracticeRibbon';
import styles from './AppLayout.module.css';

interface AppLayoutProps {
  children: ReactNode;
  /** The surfaces (Today; Verify and Release sitting for Oversight). */
  primary: NavItem[];
  /** 13.2-11: the pinned pages (Registers, Lodge), in the bar after More. */
  pinned?: NavItem[];
  /** The existing pages, under "More". */
  more: NavItem[];
  userDisplayName: string;
  roleLabel: string;
  onLogout: () => void;
}

/**
 * The shell (13-08), in the approved mock's language: the practice ribbon and the app bar stay
 * at the top, and the page owns everything below. The product is "Harmony QMS" for everyone.
 * React Aria links (the More menu) navigate through react-router, so nothing reloads the page.
 */
export default function AppLayout({ children, primary, pinned, more, userDisplayName, roleLabel, onLogout }: AppLayoutProps) {
  const navigate = useNavigate();
  return (
    <RouterProvider navigate={navigate} useHref={useHref}>
      <div className={styles.layout}>
        <a href="#main" className={styles.skip}>
          Skip to content
        </a>
        <div className={styles.top}>
          <PracticeRibbon />
          <AppBar
            primary={primary}
            pinned={pinned}
            more={more}
            userDisplayName={userDisplayName}
            roleLabel={roleLabel}
            onLogout={onLogout}
          />
        </div>
        <main id="main" className={styles.main} tabIndex={-1}>
          {children}
        </main>
      </div>
    </RouterProvider>
  );
}
